import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Feather, X, ArrowRight } from "lucide-react";
import { endpoints } from "@/lib/api";
import { N8 } from "@/components/BrandMark";
import N8Emblem from "@/components/N8Emblem";

/**
 * CompassPatternNudge — quietly surfaces one growth edge from the member's
 * Compass that appears to be showing up across their recent Journal.
 *
 * Guardrails:
 *  - Only appears when the backend returns has_nudge=true (≥2 evidence
 *    entries in the last 21 days). Otherwise renders nothing.
 *  - Language stays invitational: "may be showing up", "does this
 *    resonate?". Never diagnostic.
 *  - Dismissable for the current day via localStorage so it stops
 *    interrupting once the member has seen it.
 *  - Kept visually distinct from Soul Work — this is Compass-side, and it
 *    simply *offers* Soul Work as the next room to enter.
 */
export default function CompassPatternNudge({ variant = "card", onExplored }) {
  const [nudge, setNudge] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  const dismissedKey = "compass-pattern-nudge-dismissed";
  const isDismissedToday = () => {
    try {
      const v = localStorage.getItem(dismissedKey);
      return v && v === new Date().toISOString().slice(0, 10);
    } catch { return false; }
  };

  useEffect(() => {
    let alive = true;
    if (isDismissedToday()) { setLoaded(true); return; }
    (async () => {
      try {
        const { data } = await endpoints.soulCompassPatternNudge();
        if (!alive) return;
        setNudge(data?.has_nudge ? data : null);
      } catch {
        /* silent — a nudge failing is not a real error */
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(dismissedKey, new Date().toISOString().slice(0, 10)); } catch {}
    setNudge(null);
  };

  const markNotAccurate = async () => {
    if (!nudge) return;
    try {
      await endpoints.soulCompassNudgeDismiss({
        edge: nudge.edge,
        strength: nudge.strength,
        kind: "not_accurate",
      });
    } catch { /* silent */ }
    // Also clear the local dismissal so if a different edge surfaces later
    // today, we're ready to show it.
    try { localStorage.removeItem(dismissedKey); } catch {}
    setNudge(null);
  };

  if (!loaded || !nudge) return null;

  const compact = variant === "compact";
  return (
    <div
      className={`rounded-md border hairline bg-[hsl(var(--sand))]/30 light-halo-sm ${compact ? "p-4" : "p-5 md:p-6"}`}
      data-testid="compass-pattern-nudge"
    >
      <div className="flex items-start gap-3">
        {/* Guide breathing emblem — this card is a Guide-generated
            insight, so it carries the breathing N8 Guide mark instead
            of a plain decorative icon. */}
        <N8Emblem
          mode="simplified"
          size={34}
          breathing
          ariaLabel="N8 Guide emblem"
          testid="compass-pattern-nudge-emblem"
          className="shrink-0 mt-0.5"
        />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.26em] text-[hsl(var(--clay))]">
            Something worth noticing
          </div>
          <p className={`mt-1 leading-relaxed text-[hsl(var(--fg))] ${compact ? "text-[13px]" : "text-[15px]"}`}>
            Over your recent Journal entries, a growth edge from your
            {" "}{(nudge.archetype || "archetype").replace(/^The\s+/, "")} may be showing up:
          </p>
          <div className="mt-2 rounded-md bg-[hsl(var(--paper))] border hairline p-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">
              {nudge.strength}
              <span className="text-[hsl(var(--muted-foreground))] normal-case tracking-normal"> ·  growth edge</span>
            </div>
            <p className="mt-1 text-sm text-[hsl(var(--fg))] italic">
              &ldquo;{nudge.edge}&rdquo;
            </p>
            {!compact && Array.isArray(nudge.snippets) && nudge.snippets.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {nudge.snippets.slice(0, 2).map((s) => (
                  <li key={s.id} className="text-[12px] text-[hsl(var(--muted-foreground))] leading-relaxed">
                    &ldquo;{s.snippet}&rdquo;
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className={`mt-3 text-xs text-[hsl(var(--muted-foreground))] italic`}>
            Does this resonate? <N8 /> will hold it gently either way.
          </p>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => {
                dismiss();
                onExplored?.();
                navigate("/rituals?soul=work");
              }}
              data-testid="compass-pattern-nudge-explore"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--clay))] text-[hsl(var(--paper))] text-xs uppercase tracking-[0.22em] tab-link hover:opacity-90"
            >
              <ArrowRight size={11} /> Explore Through Soul Work
            </button>
            <button
              type="button"
              onClick={dismiss}
              data-testid="compass-pattern-nudge-dismiss"
              className="text-xs text-[hsl(var(--muted-foreground))] underline hover:no-underline"
            >
              Not right now
            </button>
            <button
              type="button"
              onClick={markNotAccurate}
              data-testid="compass-pattern-nudge-not-accurate"
              className="text-xs text-[hsl(var(--muted-foreground))] underline hover:no-underline ml-auto"
              title="Tell N8 to stop suggesting this edge for you"
            >
              This doesn&rsquo;t feel accurate
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss nudge"
          data-testid="compass-pattern-nudge-close"
          className="p-1 -m-1 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );
}
